import {
  Agent,
  CheckoutSession,
  CreditCardStatus,
  getCreditCardDetailsById,
  LoanStatusExternal,
  PaymentIntent,
  PaymentMethodType,
} from '@nemuru-eng/js-api-client';

import { PaymentMethodDetailEntity } from '@/components/modules/CheckoutSession/Detail/CheckoutSessionDetail/PaymentMethods/PaymentMethod/_shared/entities/PaymentMethodDetail/PaymentMethodDetailEntity';
import { AbstractProgressWorkflow } from '@/components/modules/CheckoutSession/Detail/CheckoutSessionDetail/PaymentMethods/PaymentMethod/_shared/entities/ProgressWorkflow/AbstractProgressWorkflow';
import { FactoryProgressWorkflow } from '@/components/modules/CheckoutSession/Detail/CheckoutSessionDetail/PaymentMethods/PaymentMethod/_shared/entities/ProgressWorkflow/FactoryProgressWorkflow';
import { Action } from '@/components/modules/CheckoutSession/Detail/CheckoutSessionDetail/PaymentMethods/PaymentMethod/_shared/types/PendingAction';
import {
  CreditCard,
  CreditCardConfigurationType,
  CreditCardOnboardingDetails,
  CreditCardWorkflow,
} from '@/config/types';

export class CreditCardPaymentMethodDetailEntity extends PaymentMethodDetailEntity {
  static allowed = [PaymentMethodType.CREDIT_CARD];

  protected _creditCard: CreditCard | undefined;
  protected _workflow: CreditCardWorkflow | undefined;
  protected _progress?: AbstractProgressWorkflow;

  public static readonly CREDIT_CARD_STATUS_FINAL = [
    CreditCardStatus.CONFIRMED,
    CreditCardStatus.CAPTURED,
    CreditCardStatus.PAID,
    CreditCardStatus.DENIED,
    CreditCardStatus.CANCELLED,
    CreditCardStatus.DESISTED,
  ];

  constructor(
    paymentMethodType: PaymentMethodType,
    checkoutSession: CheckoutSession,
    agent: Agent,
    paymentIntent?: PaymentIntent,
  ) {
    super(paymentMethodType, checkoutSession, agent, paymentIntent);
  }

  public static isAllowed(type: PaymentMethodType): boolean {
    return this.allowed.includes(type);
  }

  public async refresh(): Promise<this> {
    if (!this.paymentMethodId) {
      this._isRefreshed = true;
      return this;
    }

    try {
      this._creditCard = await getCreditCardDetailsById({ id: this.paymentMethodId });
      this._workflow = this._creditCard?.borrower.workflow;
      this._progress = await this.getProgress();
      this._isRefreshed = true;
      this.setError(undefined);
      return this;
    } catch (error) {
      this.setError(error as Error);
      console.error(error);
      return this;
    }
  }

  public shouldCreatePaymentIntent(): boolean {
    if (this.isCheckoutSessionFinal(this.checkoutSession.status)) return false;
    if (!this.paymentMethodId) return true;
    if (this.isExpired()) return true;
    return false;
  }

  public async isActionCompleted(action: Action): Promise<boolean> {
    if (!this._creditCard) {
      return false;
    }

    // TODO: Implement -- start
    if (action === Action.PAY_FIRST_INSTALMENT) {
      return this.isConfirmed();
    }
    // TODO: Implement -- end

    if (this.isFinalStatus()) {
      return true;
    }

    return false;
  }

  public clone(): this {
    throw new Error('Method not implemented.');
  }

  public isConfirmed(): boolean {
    return !!this.status && this.status === LoanStatusExternal.CONFIRMED;
  }

  public get creditCard(): CreditCard | undefined {
    return this._creditCard;
  }

  public get configuration(): CreditCardConfigurationType | undefined {
    return this._creditCard?.configuration;
  }

  public get onboardingDetails(): CreditCardOnboardingDetails | undefined {
    return this._creditCard?.borrower?.onboardingDetails;
  }

  protected async getProgress(): Promise<AbstractProgressWorkflow> {
    if (!this._creditCard?.status) {
      throw new Error('CreditCard is not set to get progress');
    }

    return FactoryProgressWorkflow.create(this._creditCard.status, this._creditCard.borrower.workflow, this.type);
  }

  public get progress(): AbstractProgressWorkflow | undefined {
    return this._progress;
  }
}
